import { Code, ConnectError, createClient, Interceptor } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { AuthenticationService } from '../gen/proto/api/gateway/v1/auth_pb';
import { LocationService } from '../gen/proto/api/gateway/v1/location_pb';
import { InstallationService } from '../gen/proto/api/gateway/v1/installation_pb';
import { InstallationDataService } from '../gen/proto/api/gateway/v1/installation_data_pb';
import { InstallationStateService } from '../gen/proto/api/gateway/v1/installation_state_pb';
import { DeviceService } from '../gen/proto/api/gateway/v1/device_pb';
import useAuthStore from '../stores/auth.store';
import useTenantStore from '../stores/tenant.store';
import { authApi } from './auth.api';
import { UserManagementService } from '../gen/proto/api/gateway/v1/user_pb';
import { FileService } from '../gen/proto/api/gateway/v1/file_pb';
import { DeviceTrackerService } from '../gen/proto/api/gateway/v1/device_tracker_pb';
import { TenantService } from '../gen/proto/api/gateway/v1/tenant_pb';
import { AlertService } from '../gen/proto/api/gateway/v1/alert_pb';

const authInterceptor: Interceptor = (next) => async (req) => {
  const token = useAuthStore.getState().authToken;
  if (token) {
    req.header.set('Authorization', `Bearer ${token}`);
  }

  try {
    return await next(req);
  } catch (error) {
    if (error instanceof ConnectError && error.code === Code.Unauthenticated) {
      const refreshedToken = await authApi.refreshToken();
      if (refreshedToken) {
        req.header.set('Authorization', `Bearer ${refreshedToken}`);
        return await next(req);
      }

      // If token refresh fails, clear auth state and redirect to login
      await useAuthStore.getState().logout();
      window.location.href = '/login';
    }

    throw error;
  }
};
const tenantInterceptor: Interceptor = (next) => async (req) => {
  const { isSuperAdmin } = useAuthStore.getState();
  const { selectedTenant } = useTenantStore.getState();
  if (isSuperAdmin) {
    req.header.set('X-Tenant-Uuid', selectedTenant);
  }

  return await next(req);
};
const transport = createConnectTransport({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  interceptors: [authInterceptor, tenantInterceptor],
});

const authTransport = createConnectTransport({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8080',
});

export const tenantClient = createClient(TenantService, transport);
export const deviceClient = createClient(DeviceService, transport);
export const deviceTrackerClient = createClient(DeviceTrackerService, transport);
export const authClient = createClient(AuthenticationService, authTransport);
export const locationClient = createClient(LocationService, transport);
export const installationClient = createClient(InstallationService, transport);
export const installationDataClient = createClient(InstallationDataService, transport);
export const installationStateClient = createClient(InstallationStateService, transport);
export const userStateClient = createClient(UserManagementService, transport);
export const fileClient = createClient(FileService, transport);
export const alertClient = createClient(AlertService, transport);
